import * as React from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import AuradineLogo from "../../../assets/auradine_logo.png";
import AuradineLogoLight from "../../../assets/auradine_logo_light.png";
import Tooltip from "@mui/material/Tooltip";
import {
  ClickAwayListener,
  createTheme,
  ThemeProvider,
  Typography,
  ButtonGroup,
  Button,
  Popover,
  Switch,
  FormControlLabel,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { useHistory } from "react-router-dom";
import {
  StyledToggleButtonGroup,
  StyledToggleButton,
} from "../../StyledComponents/Buttons/AuradineButtons";
import { useAuth0 } from "@auth0/auth0-react";
import { Popper } from "@mui/material";
import styled from "@emotion/styled";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import {
  SecondaryButton,
  StyledButtonGroup,
} from "../../StyledComponents/Buttons/AuradineButtons";
import PrivacyAndTermsDialog from "../../privacyAndTermsDialog";
import useNavigationContext from "../context/useNavigationContext";
import {
  H8TitleMedium,
  LabelSmall,
  LabelMedium,
} from "../../StyledComponents/Typography/Typography.tsx";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import useAppContextProvider from "../../../AppContext/useAppContextProvider";
import { useMutation } from "@tanstack/react-query";
import { PostUserColorTheme } from "../../../api/api";

const StyledPopoverContent = styled(Box)(({ colors }) => ({
  display: "flex",
  padding: "1rem 1.5rem",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "0.5rem",
  borderRadius: "1rem",
  boxShadow:
    "0px 2px 6px 2px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)",
}));

const ProfileCardNameTypography = styled(Typography)(({ colors }) => ({
  color: colors?.text?.label,
  textAlign: "center",
  fontFamily: "Roboto",
  fontSize: "1.375rem",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "1.75rem",
}));

const ProfileCardEmailTypography = styled(Typography)(({ colors }) => ({
  color: colors?.text?.label,
  textAlign: "center",
  fontFamily: "Roboto",
  position: "relative",
  fontSize: "0.875rem",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "1.25rem",
}));

const StyledPopover = styled(Popover)(({ colors }) => ({
  "& .MuiPaper-root": {
    borderRadius: "1rem",
    backgroundColor: colors?.background?.popover,
  },
}));

const ProfileCardButtonGroupConfig = (navigate, logout, handleClose) => {
  const { setSelectedRouteIndex } = useNavigationContext();
  return [
    {
      buttonText: "Edit account",
      onClickFunc: () => {
        navigate.push("/user");
        handleClose();
        setSelectedRouteIndex(null);
      },
      startIcon: <EditOutlinedIcon />,
    },
    {
      buttonText: "Sign out",
      onClickFunc: () => {
        logout({ returnTo: window.location.origin });
        handleClose();
      },
      startIcon: <LogoutOutlinedIcon />,
    },
  ];
};

const ProfileCard = (props) => {
  const { open, setOpen, handleClose, anchorRef } = props;
  const [privacyDialogOpen, setPrivacyDialogOpen] = React.useState(false);
  const [termsDialogOpen, setTermsDialogOpen] = React.useState(false);
  const { colors } = useTheme();
  const { setToastMessage, setToastOpen, authToken } = useAppContextProvider();
  const { logout, user } = useAuth0();
  const navigate = useHistory();
  const { toggleTheme, currentTheme } = useTheme();
  const buttonConfig = ProfileCardButtonGroupConfig(
    navigate,
    logout,
    handleClose
  );

  const mutation = useMutation({
    mutationFn: ({ token, data }) => PostUserColorTheme(token, data),
    onSuccess: async (response) => {
      console.log('User theme updated.')
    },
    onError: async (error) => {
      console.error("Error updating user theme:", error);
      setToastOpen(true);
      setToastMessage(`Failed to update user theme: ${error.message}`);
    },
  });

  const handleChangeTheme = (newTheme) => {
    console.log("newTheme", newTheme);
    const isValidTheme = (theme) => {
      return theme === "dark" || theme === "light";
    };
    if (newTheme !== null && isValidTheme(newTheme)) {
      toggleTheme(newTheme);
      mutation.mutate({
        token: authToken,
        data: { platform: "web", theme: newTheme },
      });
      handleClose();
    } else {
      currentTheme ? toggleTheme(currentTheme) : toggleTheme("light");
    }
  };

  return (
    <>
      <StyledPopover
        colors={colors}
        sx={{ zIndex: 9999 }}
        open={open}
        anchorEl={anchorRef.current}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <StyledPopoverContent colors={colors}>
            <Box display={"flex"} flexDirection={"column"}>
              <ProfileCardNameTypography colors={colors}>
                {user?.nickname}
              </ProfileCardNameTypography>
              <ProfileCardEmailTypography colors={colors}>
                {user?.email}
              </ProfileCardEmailTypography>
            </Box>
            <Box
              pb={"0.5rem"}
              pt={"0.5rem"}
              display={"flex"}
              flexDirection={"column"}
              gap={"1rem"}
            >
              <StyledButtonGroup colors={colors}>
                {buttonConfig?.map((button, index) => (
                  <Button
                    key={index}
                    sx={{
                      textTransform: "none",
                      color: colors?.text?.label,
                    }}
                    onClick={button.onClickFunc}
                    startIcon={button.startIcon}
                  >
                    {button?.buttonText}
                  </Button>
                ))}
              </StyledButtonGroup>

              {/* Theme Toggle Switch */}
              <Box display="flex" alignItems="center" justifyContent="center">
                <StyledToggleButtonGroup
                  value={currentTheme}
                  exclusive
                  onChange={(_, newTheme) => {
                    handleChangeTheme(newTheme);
                  }}
                  colors={colors}
                  sx={{
                    borderRadius: "999px",
                    overflow: "hidden",
                  }}
                >
                  <StyledToggleButton value="light" colors={colors}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        justifyContent: "center",
                        alignItems: "center",
                        flexGrow: 1,
                      }}
                    >
                      <LightModeIcon
                        sx={{
                          fontSize: 18,
                          color: colors?.text?.label,
                        }}
                      />
                      <LabelMedium>Light</LabelMedium>
                    </Box>
                  </StyledToggleButton>
                  <StyledToggleButton value="dark" colors={colors}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "0.5rem",
                        justifyContent: "center",
                        alignItems: "center",
                        flexGrow: 1,
                      }}
                    >
                      <DarkModeIcon
                        sx={{
                          fontSize: 18,
                            color: colors?.text?.label,
                        }}
                      />
                      <LabelMedium>Dark</LabelMedium>
                    </Box>
                  </StyledToggleButton>
                </StyledToggleButtonGroup>
              </Box>

              <Box display={"flex"} flexDirection={"row"}>
                <SecondaryButton
                  colors={colors}
                  onClick={() => {
                    setPrivacyDialogOpen(true);
                    setOpen(false);
                  }}
                >
                  Privacy Policy
                </SecondaryButton>
                <SecondaryButton
                  colors={colors}
                  onClick={() => {
                    setTermsDialogOpen(true);
                    setOpen(false);
                  }}
                >
                  Terms of service
                </SecondaryButton>
              </Box>
            </Box>
          </StyledPopoverContent>
        </ClickAwayListener>
      </StyledPopover>
      <PrivacyAndTermsDialog
        name="terms-2022-08"
        buttonTitle="Terms of Service"
        title="Terms of Service"
        page="../../terms.html"
        open={termsDialogOpen}
        setopen={setTermsDialogOpen}
      />
      <PrivacyAndTermsDialog
        name="privacy-2022-08"
        buttonTitle="Privacy Policy"
        title="Privacy Policy"
        page="./privacy.html"
        open={privacyDialogOpen}
        setopen={setPrivacyDialogOpen}
      />
    </>
  );
};

const AvatarMenu = () => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const anchorRef = React.useRef(null);
  const { user } = useAuth0();
  const [open, setOpen] = React.useState(false);
  const navigate = useHistory();
  const handleClick = (event) => {
    event.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };
  const { currentTheme } = useTheme();

  const handleClose = () => {
    setOpen(false);
  };
  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: 1,
          alignItems: "center",
          textAlign: "center",
          flexGrow: 1,
          width: "auto",
          padding: "0.05rem 0.1rem",
        }}
      >
        <img
          style={{ cursor: "pointer" }}
          src={
            currentTheme
              ? currentTheme === "dark"
                ? AuradineLogoLight
                : AuradineLogo
              : AuradineLogo
          }
          height="26"
          alt="logo"
        />
        <Tooltip title="Account settings">
          <IconButton
            onClick={handleClick}
            ref={anchorRef}
            size="medium"
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <Avatar
              sx={{
                width: 26,
                height: 26,
                bgcolor: "primary.dark",
              }}
            >
              {user?.name?.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>

      <ProfileCard
        open={open}
        setOpen={setOpen}
        handleClose={handleClose}
        anchorRef={anchorRef}
      />
    </React.Fragment>
  );
};

export default AvatarMenu;
